import { useEffect, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Linkedin } from 'lucide-react';
import Recommendations from '../../components/Recommendations';
import { getAirlineContent } from '../../data/airlines';

// ---------------------------------------------------------------
// Reusable airline-transition landing page.
// Routes:
//   /transition/:airline           -> EN
//   /transition/:airline/:lang     -> secondary language (e.g., fr)
// ---------------------------------------------------------------

const WhatsAppGlyph = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 .07 5.3.07 11.93c0 2.1.55 4.16 1.6 5.98L0 24l6.27-1.64a11.93 11.93 0 0 0 5.73 1.46h.01c6.62 0 11.92-5.3 11.92-11.93 0-3.19-1.24-6.18-3.41-8.41z" />
    <path d="M17.4 14.4c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.18.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.26.49 1.69.62.71.22 1.36.19 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

const AirlineTransitionPage = () => {
  const { airline: airlineSlug, lang } = useParams();
  const resolved = useMemo(
    () => getAirlineContent(airlineSlug, lang),
    [airlineSlug, lang]
  );

  // SEO meta + JSON-LD — only when content resolves
  useEffect(() => {
    if (!resolved) return;
    const { airline, content } = resolved;

    document.title = content.metaTitle;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    if (metaDesc) metaDesc.setAttribute('content', content.metaDescription);

    const existing = document.getElementById('airline-transition-schema');
    if (existing) existing.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: content.metaTitle,
      description: content.metaDescription,
      inLanguage: content.lang,
      about: {
        '@type': 'Airline',
        name: airline.airline,
      },
      provider: {
        '@type': 'Organization',
        name: 'Gate 33',
        url: 'https://gate33.co',
      },
    };
    const script = document.createElement('script');
    script.id = 'airline-transition-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('airline-transition-schema');
      if (el) el.remove();
    };
  }, [resolved]);

  if (!resolved) return <Navigate to="/" replace />;
  const { airline, content } = resolved;

  return (
    <div
      data-testid={`airline-transition-${airline.slug}-${content.lang}`}
      className="min-h-screen"
    >
      {/* Hero */}
      <section
        data-testid="airline-hero"
        className="relative min-h-[70vh] flex items-center"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={airline.heroImage}
            alt={`${airline.airline} cabin crew transitioning to private aviation with Gate 33`}
            title={`${airline.airline} cabin crew transitioning to private aviation with Gate 33`}
            className="w-full h-full object-cover object-[70%_35%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/85 to-[#FDFBF7]/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
          <div className="max-w-2xl">
            <span className="overline inline-block mb-6">{content.eyebrow}</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
              {content.title}
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed mb-10 max-w-xl">
              {content.subtitle}
            </p>
            <div className="flex flex-wrap items-center gap-5">
              <Link
                to="/academy"
                data-testid="airline-hero-cta"
                className="btn-primary inline-flex items-center gap-2"
              >
                {content.lang === 'fr' ? 'Découvrir l’Academy 3 jours' : 'Discover the 3-day Academy'}
                <ArrowRight size={16} />
              </Link>
              {content.altLangPath && content.altLangLabel && (
                <Link
                  to={content.altLangPath}
                  data-testid="airline-lang-switch"
                  className="text-xs uppercase tracking-[0.18em] text-[#1A1A1A] border-b border-[#C5A880] pb-1 hover:text-[#C5A880] transition-colors"
                >
                  {content.altLangLabel}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Intro — editorial 2-column */}
      <section
        data-testid="airline-intro"
        className="section-padding bg-white"
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5">
              <span className="overline inline-block mb-4">
                {content.lang === 'fr' ? 'Le point de départ' : 'The Starting Point'}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                {content.introTitle}
              </h2>
            </div>
            <div className="lg:col-span-7">
              {content.introText.map((p, i) => (
                <p
                  key={i}
                  className="text-[#5A5A5A] leading-relaxed mb-5 text-base lg:text-lg"
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why transition — editorial grid */}
      <section
        data-testid="airline-why"
        className="section-padding bg-[#FDFBF7]"
      >
        <div className="container-custom">
          <div className="max-w-3xl mb-14">
            <span className="overline inline-block mb-4">
              {content.lang === 'fr' ? 'Pourquoi transitionner' : 'Why Transition'}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight">
              {content.whyTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
            {content.whyItems.map((item, i) => (
              <div
                key={i}
                data-testid={`airline-why-item-${i}`}
                className="border-l-2 border-[#C5A880] pl-6 py-1"
              >
                <span className="overline text-[#C5A880] mb-3 inline-block">
                  {item.kicker}
                </span>
                <h3 className="font-heading text-xl mb-3 text-[#0F0F0F] leading-snug">
                  {item.title}
                </h3>
                <p className="text-[#5A5A5A] leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Gap — centered editorial */}
      <section
        data-testid="airline-gap"
        className="section-padding bg-white"
      >
        <div className="container-custom max-w-3xl">
          <span className="overline inline-block mb-4">
            {content.lang === 'fr' ? 'Le vrai écart' : 'The Real Gap'}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-8">
            {content.gapTitle}
          </h2>
          {content.gapText.map((p, i) => (
            <p key={i} className="text-[#5A5A5A] leading-relaxed mb-5 text-base lg:text-lg">
              {p}
            </p>
          ))}
          <div className="w-16 h-px bg-[#C5A880] mt-10" />
        </div>
      </section>

      {/* What Gate 33 brings */}
      <section
        data-testid="airline-program"
        className="section-padding bg-[#FDFBF7]"
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5">
              <span className="overline inline-block mb-4">
                {content.lang === 'fr' ? 'La méthode Gate 33' : 'The Gate 33 Method'}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
                {content.programTitle}
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed">{content.programText}</p>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {content.programItems.map((it, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-5 p-6 bg-white border border-[#E5E0D8]"
                  >
                    <span className="font-heading text-xl text-[#C5A880] flex-shrink-0 leading-none w-6">
                      0{i + 1}
                    </span>
                    <span className="text-[#1A1A1A] leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 3-Day Academy block — requested by user */}
      <section
        data-testid="airline-academy"
        className="section-padding bg-white"
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="overline inline-block mb-4">
                {content.lang === 'fr' ? 'Programme phare' : 'Flagship Program'}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] mb-8">
                {content.academyTitle}
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed mb-10 text-base lg:text-lg">
                {content.academyText}
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                {content.academyCtas.map((cta) =>
                  cta.variant === 'primary' ? (
                    <Link
                      key={cta.testId}
                      to={cta.href}
                      data-testid={cta.testId}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      {cta.label} <ArrowRight size={16} />
                    </Link>
                  ) : (
                    <Link
                      key={cta.testId}
                      to={cta.href}
                      data-testid={cta.testId}
                      className="inline-flex items-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] px-7 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-all duration-300"
                    >
                      {cta.label} <ArrowRight size={14} />
                    </Link>
                  )
                )}
              </div>
            </div>
            <div className="bg-[#F5F3EF] aspect-[5/6] flex flex-col items-center justify-center text-center px-10">
              <div
                className="font-heading text-[#C5A880]/35 leading-none mb-2"
                style={{ fontSize: 'clamp(140px, 18vw, 220px)' }}
              >
                3
              </div>
              <div className="text-[#5A5A5A] text-base tracking-wide -mt-3">
                {content.lang === 'fr' ? 'jours · en direct · avec votre formateur' : 'days · live online · with your trainer'}
              </div>
              <div className="mt-6 text-xs uppercase tracking-[0.22em] text-[#1A1A1A]">
                3-Day Online Training · €600
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CV / Spontaneous Application */}
      <section
        data-testid="airline-cv"
        className="section-padding bg-[#FDFBF7]"
      >
        <div className="container-custom max-w-3xl">
          <span className="overline inline-block mb-4">
            {content.lang === 'fr' ? 'CV & candidature' : 'CV & Application'}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-8">
            {content.cvTitle}
          </h2>
          {content.cvText.map((p, i) => (
            <p key={i} className="text-[#5A5A5A] leading-relaxed mb-5 text-base lg:text-lg">
              {p}
            </p>
          ))}
          <Link
            to={content.cvCta.href}
            data-testid={content.cvCta.testId}
            className="inline-flex items-center gap-2 mt-6 border border-[#1A1A1A] text-[#1A1A1A] px-7 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-all duration-300"
          >
            {content.cvCta.label} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Recommendations — editorial auto-fade */}
      <section
        data-testid="airline-recommendations"
        className="bg-white pb-20"
      >
        <Recommendations
          inline
          centered
          compact
          variant="light"
          eyebrow={content.lang === 'fr' ? 'Voix de la Communauté' : 'Industry Perspective'}
          title={content.lang === 'fr' ? 'Ils ont fait la transition.' : 'They made the transition.'}
          testId="airline-recommendations-block"
        />
      </section>

      {/* Community */}
      <section
        data-testid="airline-community"
        className="section-padding bg-[#FDFBF7]"
      >
        <div className="container-custom text-center max-w-3xl">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-4">
            {content.lang === 'fr' ? 'Communauté Gate 33' : 'Gate 33 Community'}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F] mb-6">
            {content.communityTitle}
          </h2>
          <p className="text-[#5A5A5A] leading-relaxed mb-10">
            {content.communityText}
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
            {content.communityCtas.map((cta) => (
              <a
                key={cta.testId}
                href={cta.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={cta.testId}
                className={
                  cta.kind === 'whatsapp'
                    ? 'inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-[#FDFBF7] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300'
                    : 'inline-flex items-center justify-center gap-2 border border-[#0F0F0F] text-[#0F0F0F] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#0F0F0F] hover:text-[#FDFBF7] transition-all duration-300'
                }
              >
                {cta.kind === 'whatsapp' ? <WhatsAppGlyph size={15} /> : <Linkedin size={15} />}
                {cta.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid="airline-final-cta-section"
        className="section-dark py-16 md:py-20 border-b border-[#C5A880]/40"
      >
        <div className="container-custom text-center max-w-3xl">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            {content.finalCtaTitle}
          </h2>
          <p className="text-[#9A9A9A] max-w-xl mx-auto mb-10">
            {content.finalCtaText}
          </p>
          <Link
            to={content.finalCtaHref}
            data-testid={content.finalCtaTestId}
            className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
          >
            {content.finalCtaLabel} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AirlineTransitionPage;
